/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import { ChevronRight, Lock } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { C, F } from '../constants/theme';
import CheckoutDisclosure from '../../NewBillingInfoPage/CheckoutDisclosure';
import { Btn, Callout, Heading, Sub, Input } from '.';
import { SanitizedHTML } from 'core/ui/atoms';
import { SET_BILLING_INFO, APPLY_PROMO_CODE } from 'core/api/apollo/mutations';
import { GET_PREPAID_PURCHASE } from 'core/api/apollo/queries';
import { updateUserQuery } from 'core/api/cache/updaters';
import GooglePayComponent from 'onramp/ui/pages/V2/NewBillingInfoPage/GooglePayComponent';
import { useProgramContext } from 'core/api/programContext';

const PaymentFormInner = ({ goPhase, onPaymentComplete, isCompletingPurchase }) => {
  const [agreed, setAgreed] = useState(false);
  const { daysLabel, bundleLabel, sizeLabel, selectedProgram } = useProgramContext();
  const stripe = useStripe();
  const elements = useElements();
  // eslint-disable-next-line no-unused-vars
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState(null);
  const [promoApplied, setPromoApplied] = useState(false);

  const { data: purchaseData, refetch: refetchPurchase } = useQuery(GET_PREPAID_PURCHASE, { fetchPolicy: 'network-only' });

  const purchase = purchaseData?.getPrepaidPurchase;
  const total = purchase?.total;
  const subtotal = purchase?.bundleReservations?.[0]?.price;
  const deliveryFee = purchase?.deliveryReservations?.[0]?.deliveryFee;
  const serviceFee = purchase?.deliveryReservations?.[0]?.serviceFee;
  const discount = purchase?.discount;
  const progLabel = selectedProgram === 'sustain' ? 'Sustain GLP-1' : selectedProgram === 'peak' ? 'Peak' : 'Signature';

  const formatAmount = (money) => money ? `$${(money.amount / 100).toFixed(2)}` : '—';

  const [applyPromoCode, { loading: applyingPromo }] = useMutation(APPLY_PROMO_CODE);

  const handleApplyPromo = useCallback(async () => {
    if (!promoCode.trim()) return;
    setPromoError(null);
    setPromoApplied(false);
    try {
      const { data } = await applyPromoCode({ variables: { input: { promoCode: promoCode.trim() } } });
      const errors = data?.applyPromoCode?.errors || [];
      if (errors.length) {
        setPromoError(errors.map((e) => e.message).join(', '));
      } else {
        setPromoApplied(true);
        await refetchPurchase();
      }
    } catch (error) {
      setPromoError(error.message || 'Failed to apply promo code.');
    }
  }, [promoCode, applyPromoCode, refetchPurchase]);

  const [setBillingInfo] = useMutation(SET_BILLING_INFO, {
    update: (cache, { data: { setBillingInfo: { billingAccount, errors } } }) => {
      updateUserQuery(cache, { user: { billingAccount } }, errors);
    },
  });

  const saveBillingInfo = useCallback(async (token) => {
    try {
      const { data } = await setBillingInfo({ variables: { input: { stripeToken: token.id } } });
      const errors = data?.setBillingInfo?.errors || [];
      if (errors.length) {
        setPaymentError(errors.map((e) => e.message).join(', '));
        setIsProcessing(false);
        setIsSubmitted(false);
      } else {
        onPaymentComplete();
      }
    } catch (error) {
      setPaymentError(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      setIsSubmitted(false);
    }
  }, [setBillingInfo, onPaymentComplete]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setIsProcessing(true);
    setPaymentError(null);

    if (!stripe || !elements) {
      setPaymentError('Payment system not ready. Please try again.');
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    const { error, token } = await stripe.createToken(cardElement);

    if (error) {
      setPaymentError(error.message);
      setIsProcessing(false);
      setIsSubmitted(false);
    } else {
      saveBillingInfo(token);
    }
  }, [stripe, elements, saveBillingInfo]);

  const handleLoadPaymentData = useCallback(async (paymentData) => {
    const { token } = paymentData.paymentMethodData.tokenizationData;
    const stripeToken = JSON.parse(token);

    setIsSubmitted(true);
    setIsProcessing(true);
    setPaymentError(null);

    if (!stripe) {
      setPaymentError('Payment system not ready. Please try again.');
      setIsProcessing(false);
      return;
    }

    try {
      await saveBillingInfo(stripeToken);
    } catch (error) {
      setPaymentError(error.message || 'Google Pay payment failed.');
      setIsProcessing(false);
      setIsSubmitted(false);
    }
  }, [stripe, saveBillingInfo]);

  const isDisabled = !cardReady || isProcessing || isCompletingPurchase || !agreed;

  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      fontFamily: F.b,
      color: C.darkBrown,
      '::placeholder': { color: '#B5AFA6' },
    },
    invalid: { color: '#c0392b' },
  };

  const stripeContainerStyle = {
    width: '100%', padding: '12px 14px', border: '1.5px solid #E8E4DC', borderRadius: 0,
    background: C.pureWhite, boxSizing: 'border-box',
  };

  return (
    <>
      <Heading size={26}>Payment</Heading>
      <Sub>Secure checkout — charged on delivery day.</Sub>

      {/* Purchase Summary */}
      <Callout style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>Purchase Summary</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: C.brown }}>
            {daysLabel}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.darkBrown }}>{formatAmount(subtotal)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: C.brown }}>
             <SanitizedHTML rawHTML={bundleLabel} />
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: C.brown }}>
            {progLabel} {sizeLabel}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: C.brown }}>Delivery</span>
          <span style={{ fontSize: 12, color: C.darkBrown }}>{formatAmount(deliveryFee)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: C.brown }}>Service Fee</span>
          <span style={{ fontSize: 12, color: C.darkBrown }}>{formatAmount(serviceFee)}</span>
        </div>
        {discount && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 12, color: C.brown }}>Discount</span>
            <span style={{ fontSize: 12, color: '#27ae60' }}>-{formatAmount(discount)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #E8E4DC', marginTop: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown }}>Total</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.darkBrown }}>{formatAmount(total)}</span>
        </div>
      </Callout>

      {/* Promo Code */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>Promo Code</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input placeholder="Promo Code" value={promoCode} onChange={setPromoCode} style={{ flex: 1 }} />
          <Btn primary={false} disabled={applyingPromo || !promoCode.trim()} onClick={handleApplyPromo} style={{ padding: '10px 20px', fontSize: 13 }}>
            {applyingPromo ? 'Applying...' : 'Apply'}
          </Btn>
        </div>
        {promoError && (
          <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{promoError}</div>
        )}
        {promoApplied && (
          <div style={{ color: '#27ae60', fontSize: 12, marginTop: 6 }}>Promo code applied successfully!</div>
        )}
      </div>

      {/* Payment Details */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: F.h, fontSize: 20, fontWeight: 500, color: C.darkBrown, marginBottom: 6 }}>Payment Details</div>
          <div style={{ fontSize: 11, color: C.lightBrown, marginBottom: 14 }}>Fields marked with * are required.</div>

          {/* Digital wallet indicators */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px', background: '#f0f0f0', marginBottom: 14 }}>
            <GooglePayComponent
              handleLoadPaymentData={handleLoadPaymentData}
              total={total}
              isDisabled={isDisabled}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.darkBrown, marginBottom: 4 }}>Card Number *</div>
              <div style={stripeContainerStyle}>
                <CardNumberElement
                  options={{ style: stripeElementStyle, placeholder: '1234 1234 1234 1234' }}
                  onReady={() => setCardReady(true)}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.darkBrown, marginBottom: 4 }}>Exp date *</div>
                <div style={stripeContainerStyle}>
                  <CardExpiryElement options={{ style: stripeElementStyle, placeholder: 'MM / YY' }} />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.darkBrown, marginBottom: 4 }}>CVC *</div>
                <div style={stripeContainerStyle}>
                  <CardCvcElement options={{ style: stripeElementStyle, placeholder: 'CVC' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {paymentError && (
          <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: '#fdecea', border: '1px solid #f5c6cb' }}>
            {paymentError}
          </div>
        )}

        <CheckoutDisclosure agreed={agreed} onAgreeChange={setAgreed} inShippingFlow={true}  />

        {/* Pay CTA */}
        <Btn type="submit" disabled={isDisabled} onClick={handleSubmit} style={{ width: '100%', marginBottom: 8, opacity: isDisabled ? 0.6 : 1 }}>
          {isCompletingPurchase ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              Setting up your plan...
            </span>
          ) : isProcessing ? 'Processing...' : <>{`Pay ${formatAmount(total)} — Start My Plan`} <ChevronRight size={15}/></>}
        </Btn>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </form>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
        <Lock size={13} color={C.lightBrown}/>
        <span style={{ fontSize: 11, color: C.lightBrown }}>Secure, encrypted payment</span>
      </div>
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 12, color: C.lightBrown, cursor: 'pointer', borderBottom: '1px solid #E8E4DC' }} onClick={() => { goPhase(8, 1); }}>Back to delivery details</span>
      </div>
    </>
  );
};

export default PaymentFormInner;
